import { pxToEm } from 'util/pxToEm';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Stack, Card, useTheme } from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useSchoolsApi } from 'services/api';
import useSWR from 'swr';
import { ApprovalTitleWithSequencesDto } from 'services/api/apis/schools/dto/approval-title-with-sequences.dto';
import { ApproverType, ApprovalFlowOrderResponse } from 'services/api/apis/schools/dto/approval-flow-order.dto';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AddApprovalSequenceModal } from 'components/modals/AddApprovalSequenceModal';
import { AddLink, Button } from 'components/ui';
import {
  colorYellowHover,
  colorDisabledBg,
  colorDisabledText,
} from 'customColors';
import EditIconSquare from '../../../assets/icons/edit.svg';

interface SortableApprovalTitleProps {
  title: ApprovalTitleWithSequencesDto;
  onEditSequence: (
    sequence: ApprovalTitleWithSequencesDto['approvalSequences'][0],
  ) => void;
}

const SortableApprovalTitle = ({
  title,
  onEditSequence,
}: SortableApprovalTitleProps) => {
  const theme = useTheme();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: title.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        border: `1px solid ${theme.palette.neutral.dark}`,
        bgcolor: '#fff',
        borderRadius: '8px',
        boxShadow: 'none',
        p: { xs: '12px', sm: '16px' },
      }}
    >
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Box display="flex" alignItems="center" mb={12}>
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              mr: 12,
              '&:hover': { color: 'text.primary' },
            }}
          >
            <DragIndicator sx={{ fontSize: 20 }} />
          </Box>
          <Typography fontWeight={600} fontSize={pxToEm(16)} color="#000">
            {title.title}
          </Typography>
        </Box>

        <Stack spacing={8} ml={32}>
          {title.approvalSequences.length > 0 ? (
            title.approvalSequences.map(sequence => (
              <Box
                key={sequence.id}
                display="flex"
                alignItems="center"
                width="100%"
              >
                <Typography
                  fontWeight={400}
                  fontSize={pxToEm(15)}
                  color="#000"
                  sx={{ flexShrink: 0 }}
                >
                  {sequence.user.firstName} {sequence.user.lastName}
                </Typography>
                <Box
                  flex={1}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography
                    fontWeight={400}
                    fontSize={pxToEm(14)}
                    color="#000"
                    sx={{
                      '&:hover': {
                        color: 'text.primary',
                      },
                    }}
                  >
                    {sequence.emailAddress}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  gap={4}
                  alignItems="center"
                  sx={{ cursor: 'pointer', flexShrink: 0 }}
                  onClick={() => onEditSequence(sequence)}
                >
                  <EditIconSquare />
                  <Typography fontSize={pxToEm(14)} color="#000">
                    Edit
                  </Typography>
                </Box>
              </Box>
            ))
          ) : (
            <Typography
              fontWeight={400}
              fontSize={pxToEm(14)}
              color={theme.palette.text.secondary}
            >
              No users added
            </Typography>
          )}
        </Stack>
      </Box>

      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <Box display="flex" alignItems="center" mb={12}>
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              mr: 8,
              '&:hover': { color: 'text.primary' },
            }}
          >
            <DragIndicator sx={{ fontSize: 20 }} />
          </Box>
          <Typography fontWeight={600} fontSize={pxToEm(14)} color="#000">
            {title.title}
          </Typography>
        </Box>

        <Stack spacing={12} ml={28}>
          {title.approvalSequences.length > 0 ? (
            title.approvalSequences.map(sequence => (
              <Box key={sequence.id}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={4}
                >
                  <Typography
                    fontWeight={400}
                    fontSize={pxToEm(14)}
                    color="#000"
                  >
                    {sequence.user.firstName} {sequence.user.lastName}
                  </Typography>
                  <Box
                    display="flex"
                    gap={4}
                    alignItems="center"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => onEditSequence(sequence)}
                  >
                    <EditIconSquare />
                    <Typography fontSize={pxToEm(13)} color="#000">
                      Edit
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  fontWeight={400}
                  fontSize={pxToEm(13)}
                  color="#000"
                  sx={{
                    '&:hover': {
                      color: 'text.primary',
                    },
                  }}
                >
                  {sequence.emailAddress}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography
              fontWeight={400}
              fontSize={pxToEm(13)}
              color={theme.palette.text.secondary}
            >
              No users added
            </Typography>
          )}
        </Stack>
      </Box>
    </Card>
  );
};

export interface ApprovalSequenceProps {
  schoolId: string;
  approvalFlowOrder?: ApprovalFlowOrderResponse | null;
}

export const ApprovalSequence = ({
  schoolId,
  approvalFlowOrder: approvalFlowOrderProp,
}: ApprovalSequenceProps): ReactElement => {
  const theme = useTheme();
  const [approvalTitles, setApprovalTitles] = useState<
    ApprovalTitleWithSequencesDto[]
  >([]);
  const [originalData, setOriginalData] = useState<
    ApprovalTitleWithSequencesDto[]
  >([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSequence, setEditingSequence] = useState<
    ApprovalTitleWithSequencesDto['approvalSequences'][0] | null
  >(null);
  const [isSaving, setIsSaving] = useState(false);

  const schoolsApi = useSchoolsApi();
  const { enqueueSnackbar } = useSnackbar();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const { data: approvalData, mutate } = useSWR(
    schoolId ? `/schools/${schoolId}/approval-sequences` : null,
    async () => {
      if (!schoolId) return null;
      const result = await schoolsApi.getApprovalSequences(schoolId);
      if (result.success) return result.value;
      throw new Error(result.error.message);
    },
  );

  useEffect(() => {
    if (approvalData) {
      setApprovalTitles(approvalData);
      setOriginalData(approvalData);
    }
  }, [approvalData]);

  const hasChanges = useMemo(() => {
    if (originalData.length === 0 || approvalTitles.length === 0) return false;
    return JSON.stringify(approvalTitles) !== JSON.stringify(originalData);
  }, [approvalTitles, originalData]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = approvalTitles.findIndex(item => item.id === active.id);
    const newIndex = approvalTitles.findIndex(item => item.id === over.id);

    if (oldIndex === newIndex) return;

    const newTitles = arrayMove(approvalTitles, oldIndex, newIndex);

    const updatedTitles = newTitles.map((title, index) => ({
      ...title,
      order: index + 1,
    }));

    setApprovalTitles(updatedTitles);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const orderUpdates = approvalTitles.map((title, index) => ({
        titleId: title.id,
        newOrder: index + 1,
      }));

      const result = await schoolsApi.updateApprovalTitleOrder(
        schoolId,
        orderUpdates,
      );

      if (result.success) {
        enqueueSnackbar('Approval workflow updated successfully', {
          variant: 'success',
        });
        await mutate();
      } else {
        enqueueSnackbar(result.error.message, { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to update approval workflow', {
        variant: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingSequence(null);
  };

  const handleModalSuccess = async () => {
    await mutate();
    handleModalClose();
  };

  const handleEditSequence = (
    sequence: ApprovalTitleWithSequencesDto['approvalSequences'][0],
  ) => {
    setEditingSequence(sequence);
    setIsModalOpen(true);
  };

  const approverTypeLabels: Record<string, string> = {
    [ApproverType.ORG_ADMIN]: 'Organization Admin',
    [ApproverType.FACILITY_APPROVERS]: 'Facilities',
    [ApproverType.CATEGORY_APPROVERS]: 'Setup & Services Approvers',
    [ApproverType.SEQUENCE_APPROVERS]: 'Sequential Approvers',
  };

  const orderedApprovalSections = useMemo(() => {
    const defaultOrder: Array<{ type: ApproverType; label: string }> = [
      { type: ApproverType.ORG_ADMIN, label: 'Organization Admin' },
      { type: ApproverType.FACILITY_APPROVERS, label: 'Facilities' },
      { type: ApproverType.CATEGORY_APPROVERS, label: 'Setup & Services Approvers' },
      { type: ApproverType.SEQUENCE_APPROVERS, label: 'Sequential Approvers' },
    ];
    if (!approvalFlowOrderProp?.items) return defaultOrder;

    return approvalFlowOrderProp.items
      .sort((a, b) => a.order - b.order)
      .map(item => ({
        type: item.approverType,
        label: approverTypeLabels[item.approverType],
      }))
      .filter(item => item.label);
  }, [approvalFlowOrderProp]);

  if (!approvalData) {
    return (
      <Box mt={20} textAlign="center">
        <Typography>Loading approval sequences...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={{ xs: 16, md: 20 }}>
        <Typography
          fontSize={{ xs: pxToEm(13), sm: pxToEm(14) }}
          fontWeight={400}
          color={theme.palette.text.primary}
        >
          Sort approvers in the required order to ensure compliance and clarity.
        </Typography>
      </Box>

      <Stack spacing={12} mb={24}>
        {orderedApprovalSections.map(section =>
          section.type === ApproverType.SEQUENCE_APPROVERS ? (
            <Box key={section.type}>
              <Box mb={12}>
                <Typography
                  fontWeight={600}
                  fontSize={{ xs: pxToEm(15), md: pxToEm(16) }}
                  color="#000"
                >
                  Sequential Approvers
                </Typography>
              </Box>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={approvalTitles.map(title => title.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack spacing={12}>
                    {approvalTitles.map(title => (
                      <SortableApprovalTitle
                        key={title.id}
                        title={title}
                        onEditSequence={handleEditSequence}
                      />
                    ))}
                  </Stack>
                </SortableContext>
              </DndContext>

              <Box mt={32}>
                <AddLink
                  onClick={() => {
                    setIsModalOpen(true);
                  }}
                >
                  Add another approver
                </AddLink>
              </Box>
            </Box>
          ) : (
            <Card
              key={section.type}
              sx={{
                border: `1px solid ${theme.palette.neutral.dark}`,
                bgcolor: '#fff',
                borderRadius: '8px',
                boxShadow: 'none',
                p: { xs: '12px', sm: '16px' },
              }}
            >
              <Typography
                fontWeight={600}
                fontSize={{ xs: pxToEm(14), md: pxToEm(16) }}
                color="#000"
              >
                {section.label}
              </Typography>
            </Card>
          ),
        )}
      </Stack>

      <Box mt={32}>
        <Button
          variant="vibrant"
          onClick={handleSave}
          disabled={isSaving || !hasChanges}
          sx={{
            display: 'flex',
            width: { xs: '100%', sm: '300px' },
            height: '47px',
            minHeight: '47px',
            maxHeight: '47px',
            padding: '16px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            bgcolor: theme.palette.vibrant.primary,
            color: '#000',
            fontWeight: 500,
            fontSize: pxToEm(14),
            textTransform: 'none',
            borderRadius: '6px',
            '&:hover': {
              bgcolor: colorYellowHover,
            },
            '&:disabled': {
              bgcolor: colorDisabledBg,
              color: colorDisabledText,
            },
          }}
        >
          {isSaving ? 'Saving...' : 'Save Approval Workflow'}
        </Button>
      </Box>

      {isModalOpen && (
        <AddApprovalSequenceModal
          open={isModalOpen}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
          schoolId={schoolId}
          editingSequence={editingSequence}
        />
      )}
    </Box>
  );
};
